import { toast } from "react-toastify";
import { Fragment } from "react";
import { forwardRef } from "react";
import { ConvertToFloat, isValidArray } from "../Layout/Common/common";
import { ImageWithDefault } from "../Layout/Common/imageWithDefault";
import "../assets/css/cartToast.css";
import { PAGES } from "../routeConfig";
import { Link, useMatch, useNavigate } from "react-router-dom";
import { axiosInstance } from "../axios/axiosInstance";
import { useAuth } from "../Context/AuthContext";

// State for ADD toast
const addToastState = { products: [], toastId: null, timer: null };

// State for DEC toast
const decToastState = { products: [], toastId: null, timer: null };

/**
 * Cart Notification Component
 * Displays accumulated products as rows
 */
export const CartNotification = forwardRef((props, ref) => {
  const { orderId, product, type = "ADD", onClose } = props;
  const { role } = useAuth();
  return (
    <div key={type} ref={ref} className="toast-body cart-toast">
      <div className="cart-item-update">
        <h6 className={`text-uppercase fw-semibold ${type === "DEC" ? "item-removed" : ""}`}>
          <i className={`fa-solid ${type === "DEC" ? "fa-trash" : "fa-check"}`} />
          <span className="notification-title">
            {type === "DEC" ? "Item Removed from your cart" : "Item Added to your cart"}
          </span>
        </h6>{" "}
        <button type="button" className="btn-close" onClick={() => onClose()} aria-label="Close" />
      </div>

      {isValidArray(product) ? (
        <div style={{ maxHeight: 400, overflow: "auto" }}>
          {product.map((item, index) => (
            <CartItem key={index} item={item} />
          ))}
        </div>
      ) : (
        <CartItem item={product} />
      )}
      {type === "DEC" ? (
        <div className="cart-toast-btn mt-3">
          <a role="button" className="viewcart-link continue-shop" onClick={onClose}>
            Continue Shopping
          </a>
        </div>
      ) : role == "3" && orderId != null ? (
        <div className="cart-toast-btn mt-3">
          <a role="button" className="viewcart-link continue-shop" onClick={onClose}>
            Continue Shopping
          </a>
        </div>
      ) : (
        <div className="cart-toast-btn mt-3">
          <a role="button" className="keepshopping" onClick={onClose}>
            Keep Shopping
          </a>
          <Link to={PAGES.CHECKOUT + (orderId ? `/${orderId}` : "")} onClick={onClose} className="checkoutcart-link">
            Checkout
          </Link>
        </div>
      )}
    </div>
  );
});

CartNotification.displayName = "CartNotification";

/**
 * Individual product item component
 */
const CartItem = ({ item }) => (
  <Fragment>
    <div className="cart-toast-details mt-3" style={{ justifyContent: "space-between", gap: 5 }}>
      <div className="pro-toast-details p-0" style={{ width: "80%" }}>
        <ImageWithDefault src={item.prodImage || item.image_th} alt="product" />
        <div className="pro-toast-qty">
          <span>{item.description}</span>
          <div className="p-0 added-cartPro-item">
            <p>
              <span>Code: {item.productCode || item.prod_code}</span>
              <span>Qty: {item.qty}</span>
            </p>
          </div>
        </div>
      </div>
      <div style={{ width: "20%", textAlign: "right" }}>
        <p className="fw-bold">${ConvertToFloat(item.price || item.price_inc_applied)}</p>
      </div>
    </div>
  </Fragment>
);

const clearToastState = (state) => {
  if (state.toastId !== null) {
    toast.dismiss(state.toastId);
  }
  if (state.timer) {
    clearTimeout(state.timer);
  }
  state.products = [];
  state.toastId = null;
  state.timer = null;
};

export const clearCartToast = () => clearToastState(addToastState);
export const clearDeleteToast = () => clearToastState(decToastState);

const mergeProducts = (existingProducts, newProducts) => {
  const merged = [...existingProducts];
  const items = Array.isArray(newProducts) ? newProducts : [newProducts];
  items.forEach((item) => {
    const id = item.prod_id || item.productId;
    const index = merged.findIndex((p) => (p.prod_id || p.productId) === id);
    if (index !== -1) {
      merged[index] = { ...merged[index], qty: item.qty || 1 };
    } else {
      merged.push(item);
    }
  });
  return merged;
};

/**
 * Add product to toast and display/update
 * Multiple calls will accumulate products and show them with the timer pausing on tab switch
 *
 * @param {object|array} product - Product data or array of products
 * @param {string} type - 'ADD' or 'DEC'
 * @param {function} onCheckout - Callback when checkout is clicked
 */
export const showAddItemToast = (product) => {
  addToastState.products = mergeProducts(addToastState.products, product);

  if (addToastState.timer) clearTimeout(addToastState.timer);

  if (addToastState.toastId !== null) {
    toast.update(addToastState.toastId, {
      render: (
        <CartNotification
          orderId={product.order_id}
          product={addToastState.products}
          type="ADD"
          onClose={() => clearCartToast()}
        />
      ),
      autoClose: 5000,
    });
  } else {
    addToastState.toastId = toast(
      <CartNotification
        orderId={product.order_id}
        product={addToastState.products}
        type="ADD"
        onClose={() => clearCartToast()}
      />,
      {
        autoClose: 5000,
        pauseOnFocusLoss: true,
        pauseOnHover: true,
        position: "top-right",
        hideProgressBar: true,
        className: "cartadded-toast",
        bodyClassName: "toast show",
        containerId: "cart-notification-root",
        closeButton: false,
        onClose: () => clearCartToast(),
      },
    );
  }

  addToastState.timer = setTimeout(() => {}, 6000);
};

export const showRemoveItemToast = (product) => {
  decToastState.products = mergeProducts(decToastState.products, product);

  if (decToastState.timer) clearTimeout(decToastState.timer);

  if (decToastState.toastId !== null) {
    toast.update(decToastState.toastId, {
      render: (
        <CartNotification
          orderId={product.order_id}
          product={decToastState.products}
          type="DEC"
          onClose={() => clearDeleteToast()}
        />
      ),
      autoClose: 5000,
    });
  } else {
    decToastState.toastId = toast(
      <CartNotification
        orderId={product.order_id}
        product={decToastState.products}
        type="DEC"
        onClose={() => clearDeleteToast()}
      />,
      {
        autoClose: 5000,
        pauseOnFocusLoss: true,
        pauseOnHover: true,
        position: "top-right",
        hideProgressBar: true,
        className: "cartremoved-toast",
        bodyClassName: "toast-cart",
        containerId: "cart-notification-root",
        closeButton: false,
        onClose: () => clearDeleteToast(),
      },
    );
  }

  decToastState.timer = setTimeout(() => {}, 6000);
};

export const showCartToast = (product, type = "ADD") => {
  if (type === "ADD" || type === "INC") {
    showAddItemToast(product);
  } else if (type === "DEC") {
    showRemoveItemToast(product);
  }
};

/**
 * Hook-style function - returns the showCartToast function
 * Use this in your components
 */
export const useCartToastManager = () => {
  return {
    showCartToast,
    showRemoveItemToast,
    clearCartToast,
    clearDeleteToast,
  };
};
